export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffFactor?: number;
  jitter?: boolean;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
  signal?: AbortSignal;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'shouldRetry' | 'onRetry' | 'signal'>> = {
  maxAttempts: 3,
  initialDelayMs: 500,
  maxDelayMs: 8_000,
  backoffFactor: 2,
  jitter: true,
};

const isTransientError = (error: unknown): boolean => {
  const candidate = error && typeof error === 'object' ? (error as Record<string, unknown>) : null;
  const code = typeof candidate?.code === 'string' ? candidate.code : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof candidate?.message === 'string'
        ? candidate.message
        : '';

  if (
    code === 'functions/unavailable' ||
    code === 'functions/deadline-exceeded' ||
    code === 'functions/aborted' ||
    code === 'functions/resource-exhausted' ||
    code === 'functions/internal' ||
    code === 'unavailable'
  ) {
    return true;
  }

  return /failed to fetch|load failed|networkerror|network request failed|timeout|econnreset/i.test(
    message,
  );
};

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const withRetry = async <T>(
  operation: (attempt: number) => Promise<T>,
  options: RetryOptions = {},
): Promise<T> => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const shouldRetry = options.shouldRetry ?? isTransientError;
  let lastError: unknown;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt += 1) {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      return await operation(attempt);
    } catch (error) {
      lastError = error;

      if (attempt >= config.maxAttempts || !shouldRetry(error, attempt)) {
        throw error;
      }

      const baseDelay = Math.min(
        config.maxDelayMs,
        config.initialDelayMs * Math.pow(config.backoffFactor, attempt - 1),
      );
      const delay = config.jitter ? baseDelay * (0.5 + Math.random() * 0.5) : baseDelay;

      options.onRetry?.(error, attempt, delay);
      await sleep(delay, options.signal);
    }
  }

  throw lastError;
};
